import { Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useActiveSession } from '@texas/hooks/useSession';
import { AssignCustomerComplianceDocument } from './AssignCustomerComplianceDocument';
import { CustomerComplianceDocumentsTable } from './CustomerComplianceDocumentsTable';
import { RemoveCustomerComplianceDocument } from './RemoveCustomerComplianceDocument';
import { useCustomerComplianceDocuments } from './useCustomerComplianceDocuments';

interface Props {
  customerId: number;
  customerName: string;
  customerArchived: boolean;
}

export function CustomerComplianceDocuments({
  customerId,
  customerName,
  customerArchived,
}: Props) {
  const { t } = useTranslation();
  const { branches } = useActiveSession();
  const { documents, error, loading, assignDisclosure, removeDisclosure } =
    useCustomerComplianceDocuments(customerId);

  return (
    <BodyOverride>
      <EntityDirectoryPage
        title={t('customerComplianceDocuments.title')}
        description={t('customerComplianceDocuments.description', {
          name: customerName,
        })}
        actions={
          !customerArchived && (
            <Button
              variant="entityDirectoryPrimary"
              isDisabled={loading || !!error}
              onClick={assignDisclosure.onOpen}
            >
              {t('customerComplianceDocuments.assignFromConfigs')}
            </Button>
          )
        }
      >
        {loading && <SpinnerWithLabel label={t('general.loadingData')} />}
        {!loading && (
          <>
            {!error && documents.length === 0 ? (
              <EntityDirectoryEmptyState>
                {t('customerComplianceDocuments.empty')}
              </EntityDirectoryEmptyState>
            ) : (
              <>
                {!error && (
                  <Text variant="configuration-strong">
                    {t('customerComplianceDocuments.count', {
                      count: documents.length,
                    })}
                  </Text>
                )}
                <CustomerComplianceDocumentsTable
                  customerArchived={customerArchived}
                  customerId={customerId}
                  documents={documents}
                  error={error}
                  onRemove={removeDisclosure.onOpen}
                />
                {!error && (
                  <Text variant="entity-directory-description">
                    {t('customerComplianceDocuments.removeHelp')}
                  </Text>
                )}
              </>
            )}
            {!error && assignDisclosure.isOpen && !customerArchived && (
              <AssignCustomerComplianceDocument
                key={customerId}
                customerId={customerId}
                customerName={customerName}
                branches={branches}
                documents={documents}
                onClose={assignDisclosure.onClose}
              />
            )}
            {!error && removeDisclosure.isOpen && removeDisclosure.value && (
              <RemoveCustomerComplianceDocument
                key={removeDisclosure.value.complianceDocumentDefinitionId}
                customerId={customerId}
                customerName={customerName}
                document={removeDisclosure.value}
                isOpen={removeDisclosure.isOpen}
                onClose={removeDisclosure.onClose}
              />
            )}
          </>
        )}
      </EntityDirectoryPage>
    </BodyOverride>
  );
}
